import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, useToast } from '@kss/ui';
import { ChevronDown, Plus, Upload, Download } from 'lucide-react';
import { downloadTemplate, pickAndParseXlsx, type BulkColumnSpec } from '../../utils/bulkExcel';

export interface StandardAddDropdownProps<T extends object> {
  /** e.g. "/panel/users" — `${basePath}/new` and `${basePath}/bulk` are derived. */
  basePath: string;
  newLabel: string;
  /** Module name for the template filename (e.g. "User"). */
  module: string;
  /** Field keys to surface in the bulk template. */
  templateFields: (keyof T & string)[];
  /** Service exposing `.create(items: T[])`. */
  service: { create: (items: T[]) => Promise<unknown> };
}

/**
 * Compact split-button at the top of every list page:
 *   [+ New <X>  ▾]
 *      ├── New <X>
 *      ├── Bulk add (page)
 *      ├── Download template
 *      └── Upload Excel / CSV
 */
export function StandardAddDropdown<T extends object>({
  basePath,
  newLabel,
  module,
  templateFields,
  service,
}: StandardAddDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const columns: BulkColumnSpec<T>[] = templateFields.map((k) => ({
    key: k,
    header: k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim(),
  }));

  function onTemplate() {
    downloadTemplate<T>(`${module}-template.xlsx`, columns);
    setOpen(false);
  }

  async function onUpload() {
    setOpen(false);
    setBusy(true);
    try {
      const rows = await pickAndParseXlsx<T>(columns);
      if (!rows.length) {
        toast.error('Empty file', 'No data rows detected');
        return;
      }
      await service.create(rows as T[]);
      toast.success('Imported', `${rows.length} ${module.toLowerCase()}(s) created`);
    } catch (err) {
      toast.error('Import failed', err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative inline-flex">
      <Link to={`${basePath}/new`}>
        <Button size="sm" className="rounded-r-none">
          <Plus className="h-3.5 w-3.5" /> {newLabel}
        </Button>
      </Link>
      <Button
        size="sm"
        className="rounded-l-none border-l border-white/20 px-2"
        onClick={() => setOpen((v) => !v)}
        aria-label="More add options"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-border bg-surface-card shadow-lg">
            <Link
              to={`${basePath}/new`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm hover:bg-surface-card-hover"
            >
              <Plus className="mr-2 inline h-3.5 w-3.5" /> {newLabel}
            </Link>
            <Link
              to={`${basePath}/bulk`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-sm hover:bg-surface-card-hover"
            >
              <Upload className="mr-2 inline h-3.5 w-3.5" /> Bulk add (paste rows)
            </Link>
            <button
              type="button"
              onClick={onTemplate}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-surface-card-hover"
            >
              <Download className="mr-2 inline h-3.5 w-3.5" /> Download template
            </button>
            <button
              type="button"
              onClick={onUpload}
              disabled={busy}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-surface-card-hover disabled:opacity-50"
            >
              <Upload className="mr-2 inline h-3.5 w-3.5" />
              {busy ? 'Uploading…' : 'Upload Excel / CSV'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default StandardAddDropdown;
